import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Trash2,
  Sparkles,
  Wand2,
  ThumbsUp,
  ThumbsDown,
  X,
  Paperclip,
  FileText,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import { fileToBase64, formatBytes } from '@/lib/fileToBase64';
import { OriginalMessagePanel } from '@/components/email/OriginalMessagePanel';
import { ThreadSummaryCard } from '@/components/email/ThreadSummaryCard';
import type {
  DraftAttachmentDto,
  DraftDto,
  DraftIntentSuggestion,
  DraftKind,
  DraftRegenerateInput,
  DraftStyleRating,
} from '@cc/types';

export const KIND_LABEL: Record<DraftKind, string> = {
  reply: 'Reply',
  reply_all: 'Reply all',
  forward: 'Forward',
  new: 'New',
};

/**
 * The full draft editor: original-message panel + thread summary + recipient
 * fields + body editor + save/send/discard. Used both on the Drafts page and
 * inline within the inbox MessagePanel — so reply-from-inbox keeps Karl in
 * the inbox view.
 */
export function DraftComposer({
  id,
  onSent,
  onDiscarded,
  showOriginal = true,
  compact = false,
}: {
  id: string;
  onSent?: (draft: DraftDto) => void;
  onDiscarded?: () => void;
  /** When false (e.g. the inbox already shows the message), suppress the inline original panel. */
  showOriginal?: boolean;
  /** Drop the surrounding Card chrome when used inside another panel. */
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.draft(id),
    queryFn: () => api.getDraft(id),
  });

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);

  // Intent panel state. Opens when the user clicks "AI draft" so they can
  // steer the outcome before Opus drafts. selectedIntent / customIntent are
  // mutually exclusive at submit time (custom wins).
  const [intentPanelOpen, setIntentPanelOpen] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState<string>('');
  const [customIntent, setCustomIntent] = useState<string>('');
  // Refine input — applies a tweak to the existing body without restarting.
  const [refinementText, setRefinementText] = useState<string>('');

  useEffect(() => {
    if (data?.draft) {
      setTo(data.draft.toEmails.join(', '));
      setCc(data.draft.ccEmails.join(', '));
      setBcc(data.draft.bccEmails.join(', '));
      setSubject(data.draft.subject);
      setBody(data.draft.body);
      setDirty(false);
    }
  }, [data]);

  const payload = useMemo(
    () => ({
      to: parseEmails(to),
      cc: parseEmails(cc),
      bcc: parseEmails(bcc),
      subject,
      body,
    }),
    [to, cc, bcc, subject, body],
  );

  const save = useMutation({
    mutationFn: () => api.updateDraft(id, payload),
    onSuccess: (res) => {
      queryClient.setQueryData(qk.draft(id), res);
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      setDirty(false);
      toast.success('Draft saved.');
    },
    onError: () => toast.error('Save failed.'),
  });

  const approve = useMutation({
    mutationFn: async () => {
      if (dirty) await api.updateDraft(id, payload);
      return api.approveDraft(id);
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('Sent via Gmail.');
      if (res.draft) onSent?.(res.draft);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Send failed.';
      toast.error(msg);
    },
  });

  const discard = useMutation({
    mutationFn: () => api.discardDraft(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success('Discarded.');
      onDiscarded?.();
    },
    onError: () => toast.error('Discard failed.'),
  });

  const regenerate = useMutation({
    mutationFn: (input: DraftRegenerateInput = {}) => api.regenerateDraft(id, input),
    onSuccess: (res, variables) => {
      if (res.draft) queryClient.setQueryData(qk.draft(id), { draft: res.draft });
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      setDirty(false);
      setIntentPanelOpen(false);
      setRefinementText('');
      setSelectedIntent('');
      setCustomIntent('');
      if (variables?.refinement) toast.success('Refined.');
      else if (variables?.intent) toast.success(`Drafted: ${variables.intent}`);
      else toast.success('Draft regenerated.');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Regenerate failed.';
      toast.error(msg);
    },
  });

  // Fetched only when the intent panel is open. Tanstack-query caches per id.
  const intentsQuery = useQuery({
    queryKey: ['drafts', id, 'intents'],
    queryFn: () => api.suggestDraftIntents(id),
    enabled: intentPanelOpen,
    staleTime: 5 * 60_000,
  });

  const uploadAttachment = useMutation({
    mutationFn: async (file: File) => {
      const contentBase64 = await fileToBase64(file);
      return api.uploadDraftAttachment(id, {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.draft(id) });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Upload failed.';
      toast.error(msg);
    },
  });

  const removeAttachment = useMutation({
    mutationFn: (attachId: string) => api.deleteDraftAttachment(id, attachId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.draft(id) });
    },
    onError: () => toast.error('Could not remove attachment.'),
  });

  const rate = useMutation({
    mutationFn: (next: DraftStyleRating | null) => api.rateDraft(id, next),
    onSuccess: (res) => {
      queryClient.setQueryData(qk.draft(id), res);
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
    onError: () => toast.error('Could not update style rating.'),
  });

  if (isLoading || !data) return <Skeleton className="h-80 w-full" />;
  const d = data.draft;
  const terminal = d.status === 'SENT' || d.status === 'DISCARDED';
  const meta = (d.generationMeta ?? {}) as {
    lastIntent?: string | null;
    lastRefinement?: string | null;
    confidence?: number;
  };
  const effectiveIntent = customIntent.trim() || selectedIntent;
  const inner = (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {KIND_LABEL[d.kind]}
            </Badge>
            <StatusBadge status={d.status} />
            {typeof meta.confidence === 'number' && (
              <Badge variant="outline">
                <Sparkles className="mr-1 h-3 w-3" /> confidence{' '}
                {(meta.confidence * 100).toFixed(0)}%
              </Badge>
            )}
            {d.status === 'SENT' && !compact && (
              <StyleRatingButtons
                value={d.styleRating}
                pending={rate.isPending}
                onChange={(next) => rate.mutate(next)}
              />
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            from {d.accountEmail}
            {d.message?.projectName ? ` · project ${d.message.projectName}` : ''}
          </p>
        </div>
      </div>

      {compact && d.status === 'SENT' && (
        <SentRatingBanner
          value={d.styleRating}
          pending={rate.isPending}
          onChange={(next) => rate.mutate(next)}
        />
      )}

      {showOriginal && d.messageId && d.kind !== 'new' && (
        <>
          <ThreadSummaryCard messageId={d.messageId} />
          <OriginalMessagePanel
            messageId={d.messageId}
            defaultExpanded={d.kind !== 'forward'}
          />
        </>
      )}

      {intentPanelOpen && d.messageId && d.kind !== 'new' && !terminal && (
        <IntentPanel
          intents={intentsQuery.data?.intents ?? null}
          loading={intentsQuery.isLoading}
          error={intentsQuery.error instanceof Error ? intentsQuery.error.message : null}
          selectedIntent={selectedIntent}
          customIntent={customIntent}
          onSelect={(label) => {
            setSelectedIntent(label);
            setCustomIntent('');
          }}
          onCustomChange={(value) => {
            setCustomIntent(value);
            if (value.trim()) setSelectedIntent('');
          }}
          onClose={() => setIntentPanelOpen(false)}
          onSkip={() => regenerate.mutate({})}
          onGenerate={() => {
            const intent = effectiveIntent;
            if (!intent) return;
            regenerate.mutate({ intent });
          }}
          generating={regenerate.isPending}
          canGenerate={Boolean(effectiveIntent)}
        />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[80px_1fr]">
        <Label className="sm:pt-2">To</Label>
        <Input
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setDirty(true);
          }}
          disabled={terminal}
          placeholder="comma-separated"
        />

        <Label className="sm:pt-2">Cc</Label>
        <Input
          value={cc}
          onChange={(e) => {
            setCc(e.target.value);
            setDirty(true);
          }}
          disabled={terminal}
          placeholder="optional"
        />

        <Label className="sm:pt-2">Bcc</Label>
        <Input
          value={bcc}
          onChange={(e) => {
            setBcc(e.target.value);
            setDirty(true);
          }}
          disabled={terminal}
          placeholder="optional"
        />

        <Label className="sm:pt-2">Subject</Label>
        <Input
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            setDirty(true);
          }}
          disabled={terminal}
        />
      </div>

      <Textarea
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          setDirty(true);
        }}
        className="min-h-[240px] text-[15px] leading-[1.6]"
        disabled={terminal}
      />

      <AttachmentsPanel
        draftId={id}
        attachments={d.attachments}
        terminal={terminal}
        uploading={uploadAttachment.isPending}
        onUpload={(file) => uploadAttachment.mutate(file)}
        onRemove={(attachId) => removeAttachment.mutate(attachId)}
        removingId={removeAttachment.variables}
      />

      {!terminal && body.trim().length > 0 && d.messageId && d.kind !== 'new' && (
        <RefineRow
          value={refinementText}
          onChange={setRefinementText}
          onSubmit={() => {
            const r = refinementText.trim();
            if (!r) return;
            regenerate.mutate({ refinement: r });
          }}
          pending={regenerate.isPending}
          lastIntent={meta.lastIntent ?? null}
          lastRefinement={meta.lastRefinement ?? null}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending || terminal}
          >
            {save.isPending ? 'Saving...' : dirty ? 'Save edits' : 'Saved'}
          </Button>
          {d.messageId && d.kind !== 'new' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIntentPanelOpen((o) => !o)}
              disabled={regenerate.isPending || terminal}
              title="Open the intent picker so you can steer the AI draft"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {regenerate.isPending
                ? 'Drafting...'
                : intentPanelOpen
                  ? 'Close intent'
                  : 'AI draft'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => discard.mutate()}
            disabled={discard.isPending || terminal}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" /> Discard
          </Button>
        </div>
        <Button
          onClick={() => approve.mutate()}
          disabled={
            approve.isPending ||
            terminal ||
            body.trim().length === 0 ||
            parseEmails(to).length === 0
          }
        >
          {approve.isPending ? (
            'Sending...'
          ) : (
            <>
              <Send className="h-4 w-4" /> Send
            </>
          )}
        </Button>
      </div>

      {terminal && d.sentAt && (
        <p className="text-[11px] text-muted-foreground">Sent {relativeTime(d.sentAt)}.</p>
      )}
      {terminal && d.discardedAt && (
        <p className="text-[11px] text-muted-foreground">
          Discarded {relativeTime(d.discardedAt)}.
        </p>
      )}
    </div>
  );

  if (compact) return inner;
  return (
    <Card>
      <CardContent className="p-5">{inner}</CardContent>
    </Card>
  );
}

function AttachmentsPanel({
  draftId,
  attachments,
  terminal,
  uploading,
  onUpload,
  onRemove,
  removingId,
}: {
  draftId: string;
  attachments: DraftAttachmentDto[];
  terminal: boolean;
  uploading: boolean;
  onUpload: (file: File) => void;
  onRemove: (attachId: string) => void;
  removingId: string | undefined;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  if (attachments.length === 0 && terminal) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Attachments {attachments.length > 0 && <span>({attachments.length})</span>}
        </p>
        {!terminal && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                for (const f of files) onUpload(f);
                // Reset the input so picking the same file again still triggers onChange.
                if (e.target) e.target.value = '';
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Paperclip className="h-3.5 w-3.5" /> Attach files
                </>
              )}
            </Button>
          </>
        )}
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-2.5 py-1.5"
            >
              <a
                href={`/api/drafts/${draftId}/attachments/${a.id}`}
                className="flex min-w-0 items-center gap-2 text-[12px] text-foreground hover:underline"
                download={a.filename}
              >
                <FileText className="h-3.5 w-3.5 flex-none text-muted-foreground" />
                <span className="truncate">{a.filename}</span>
                <span className="flex-none text-[10px] text-muted-foreground">
                  {formatBytes(a.sizeBytes)}
                </span>
              </a>
              {!terminal && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                  onClick={() => onRemove(a.id)}
                  disabled={removingId === a.id}
                  aria-label={`Remove ${a.filename}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// fileToBase64 + formatBytes now live in '@/lib/fileToBase64' so renovation
// media uploads can reuse them.

function IntentPanel({
  intents,
  loading,
  error,
  selectedIntent,
  customIntent,
  onSelect,
  onCustomChange,
  onClose,
  onSkip,
  onGenerate,
  generating,
  canGenerate,
}: {
  intents: DraftIntentSuggestion[] | null;
  loading: boolean;
  error: string | null;
  selectedIntent: string;
  customIntent: string;
  onSelect: (label: string) => void;
  onCustomChange: (value: string) => void;
  onClose: () => void;
  onSkip: () => void;
  onGenerate: () => void;
  generating: boolean;
  canGenerate: boolean;
}) {
  return (
    <div className="space-y-3 rounded-md border border-foreground/20 bg-muted/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[12px] font-semibold">What outcome do you want?</p>
          <p className="text-[11px] text-muted-foreground">
            Pick a chip or write your own. The AI uses this as the steer for the draft.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close intent picker"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-5/6" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      )}

      {error && !loading && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] text-destructive">
          Couldn't load suggestions: {error}
        </p>
      )}

      {intents && intents.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {intents.map((it) => {
            const active = selectedIntent === it.label;
            return (
              <button
                key={it.label}
                type="button"
                onClick={() => onSelect(it.label)}
                className={cn(
                  'rounded-md border px-2.5 py-1.5 text-left transition-colors',
                  active
                    ? 'border-foreground/40 bg-foreground/5'
                    : 'border-border hover:border-foreground/30 hover:bg-background',
                )}
              >
                <div className="text-[12px] font-medium leading-tight">{it.label}</div>
                {it.description && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {it.description}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Input
        value={customIntent}
        onChange={(e) => onCustomChange(e.target.value)}
        placeholder="Or write your own outcome..."
        className="h-8 text-[13px]"
      />

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onSkip}
          disabled={generating}
          className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          Skip and draft anyway
        </button>
        <Button
          size="sm"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
        >
          {generating ? 'Drafting...' : 'Generate'}
        </Button>
      </div>
    </div>
  );
}

function RefineRow({
  value,
  onChange,
  onSubmit,
  pending,
  lastIntent,
  lastRefinement,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  pending: boolean;
  lastIntent: string | null;
  lastRefinement: string | null;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Wand2 className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Refine: shorter, more direct, add that I'm away next week..."
          className="h-8 text-[13px]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) {
              e.preventDefault();
              onSubmit();
            }
          }}
          disabled={pending}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={onSubmit}
          disabled={!value.trim() || pending}
        >
          {pending ? '...' : 'Refine'}
        </Button>
      </div>
      {(lastIntent || lastRefinement) && (
        <p className="pl-5 text-[10px] text-muted-foreground">
          {lastIntent && <>Intent: <span className="text-foreground/80">{lastIntent}</span></>}
          {lastIntent && lastRefinement && ' · '}
          {lastRefinement && <>Last refinement: <span className="text-foreground/80">{lastRefinement}</span></>}
        </p>
      )}
    </div>
  );
}

function SentRatingBanner({
  value,
  pending,
  onChange,
}: {
  value: DraftStyleRating | null;
  pending: boolean;
  onChange: (next: DraftStyleRating | null) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3">
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-foreground">
          {value === 'GOOD'
            ? 'Saved to style memory.'
            : value === 'BAD'
              ? 'Marked as off-voice.'
              : 'How was this draft?'}
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Thumbs-up trains the AI on your voice. Top 5 rated drafts are used as exemplars.
        </p>
      </div>
      <div className="flex flex-none items-center gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={() => onChange(value === 'GOOD' ? null : 'GOOD')}
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
            value === 'GOOD'
              ? 'border-emerald-500 bg-emerald-500/25 text-emerald-700 dark:text-emerald-300'
              : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
          aria-label="Good — use as exemplar"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => onChange(value === 'BAD' ? null : 'BAD')}
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
            value === 'BAD'
              ? 'border-destructive bg-destructive/20 text-destructive'
              : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
          aria-label="Off-voice — do not use as exemplar"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function StyleRatingButtons({
  value,
  pending,
  onChange,
}: {
  value: DraftStyleRating | null;
  pending: boolean;
  onChange: (next: DraftStyleRating | null) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1" title="Use this draft as a style exemplar">
      <button
        type="button"
        disabled={pending}
        onClick={() => onChange(value === 'GOOD' ? null : 'GOOD')}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-md border text-muted-foreground transition-colors',
          value === 'GOOD'
            ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-600'
            : 'border-border hover:text-foreground',
        )}
        aria-label="Mark as good style exemplar"
      >
        <ThumbsUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={() => onChange(value === 'BAD' ? null : 'BAD')}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-md border text-muted-foreground transition-colors',
          value === 'BAD'
            ? 'border-destructive/50 bg-destructive/15 text-destructive'
            : 'border-border hover:text-foreground',
        )}
        aria-label="Mark as not representative"
      >
        <ThumbsDown className="h-3 w-3" />
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: DraftDto['status'] }) {
  const variant: 'success' | 'warning' | 'muted' | 'danger' =
    status === 'SUGGESTED'
      ? 'warning'
      : status === 'EDITED' || status === 'APPROVED'
        ? 'success'
        : status === 'SENT'
          ? 'muted'
          : 'danger';
  return <Badge variant={variant}>{status.toLowerCase()}</Badge>;
}

function parseEmails(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}
